import { useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import { showModal } from 'modal.react.js';
import { useSelector } from 'react-redux';

import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { DeviceMobile } from 'components/shared/Icon/phosphorIcons';
import AppStoreSvg from 'components/shared/images/appStore.svg';
import GooglePlaySvg from 'components/shared/images/googlePlay.svg';
import { VIEWED_MOBILE_APP_INFO, MOBILE_OPERATING_SYSTEMS } from 'constants/mobileApplication';
import useProposalPathForMobileAppBuilder from 'hooks/useProposalPathForMobileAppBuilder';
import i18n from 'i18n';
import { itinerariesItemAppSelector } from 'store/itinerariesItem/selectors';
import { isMobileBrowser } from 'utils/browserDetect';
import { copyTextToClipboard, waitForElm } from 'utils/common';
import { IProposal, TApp } from 'utils/itineraryDetails/types';
import {
  getMobileOperatingSystem,
  checkIsWebview,
  markPathAsViewed,
  checkIsViewedPath,
  getStoreUrl,
  getDeeplinkUrl,
} from 'utils/mobileApplication';

import RenderInDomNode from '../../Pages/Admin/Page/common/RenderInStuff';
import { MOB_APP_BUTTON_MOUNT_POINT } from './constants';
import s from './index.module.css';

interface IProps {
  proposal: IProposal;
  accessToken?: string | null;
  isButtonHidden?: boolean;
}

const BANNER_ID = 'mobile-app-info';

// TODO: make mobile app modal as a wrapper, so we will be able to delete all mobile logic from master proposal screens
const MobileModalContent = function ({ path, handleClose }: { path: string; handleClose: () => void }) {
  const itineraryItemApp: TApp = useSelector(itinerariesItemAppSelector);
  const OS = getMobileOperatingSystem();
  const isIOS = OS === MOBILE_OPERATING_SYSTEMS.ios;
  const [appOpening, setAppOpening] = useState(false);

  const storeUrl = getStoreUrl(itineraryItemApp);
  const deeplinkUrl = getDeeplinkUrl(itineraryItemApp, path);

  const handleClickLinkToStore = () => {
    copyTextToClipboard(deeplinkUrl);
  };

  const handleClickAlreadyHaveApp = () => {
    let deepLinkOpened = false;

    const checkIfAppOpened = () => {
      if (document.visibilityState === 'hidden') {
        deepLinkOpened = true;
      }
    };

    document.addEventListener('visibilitychange', checkIfAppOpened);
    setAppOpening(true);
    window.location.href = deeplinkUrl;

    setTimeout(() => {
      document.removeEventListener('visibilitychange', checkIfAppOpened);
      if (!deepLinkOpened) {
        const isOpenStorePage = confirm(
          i18n.t('proposal_content.mobile_app.popup.open_store_alert', { store: isIOS ? 'App Store' : 'Google Play' }),
        );

        if (isOpenStorePage) {
          if (!storeUrl) return;
          window.location.href = storeUrl;
        }
      }
      setAppOpening(false);
    }, 3000);
  };

  return (
    <>
      <div className={s.mobileAppInfoContent}>
        <h4 className={cx(s.mobileAppInfoTitle, 'custom-mobile-app-info-title')}>
          {i18n.t('proposal_content.mobile_app.popup.get_full_experience', {
            defaultValue: 'Get the full experience on the app!',
          })}
        </h4>
        <p className={cx(s.mobileAppInfoText, 'custom-mobile-app-info-text')}>
          {i18n.t('proposal_content.mobile_app.popup.launch_app', {
            defaultValue: 'Launch the app version anytime from the mobile icon in the top right.',
          })}
        </p>
        <div className={s.mobileAppInfoLinks}>
          <a href={storeUrl} target="_blank" rel="noreferrer" onClick={handleClickLinkToStore}>
            {isIOS ? (
              <img src={AppStoreSvg} alt="Download on the App Store" />
            ) : (
              <img src={GooglePlaySvg} alt="Get it on Google Play" />
            )}
          </a>
        </div>
      </div>
      <div className={s.mobileAppInfoFooter}>
        <a
          href={deeplinkUrl}
          onClick={(e) => {
            e.preventDefault();
            handleClickAlreadyHaveApp();
          }}
          className={cx(
            s.mobileAppInfoAction,
            s['mobileAppInfoAction-link'],
            appOpening && s.loading,
            'custom-mobile-app-info-btn',
          )}
        >
          {i18n.t('proposal_content.mobile_app.popup.options.already_have_app', {
            defaultValue: 'Already have the app? Click here.',
          })}
          {appOpening && <span className={s.loader} />}
        </a>
        <button type="button" className={cx(s.mobileAppInfoAction, 'custom-mobile-app-info-btn')} onClick={handleClose}>
          {i18n.t('proposal_content.mobile_app.popup.options.continue_in_browser', {
            defaultValue: 'Or, Continue in Browser',
          })}
        </button>
      </div>
    </>
  );
};

export const openMobileAppInfoModal = (path: string, isMustBeShownOnce?: boolean, paths?: string[]) => {
  // additional check to prevent multi-layer showing of banner
  // see the task https://app.asana.com/0/1205677691765880/1206148057725392/f
  if (document.getElementById(BANNER_ID)) return;

  const isAvailableForOpening = isMustBeShownOnce ? !checkIsViewedPath(path) : true;

  if (isMobileBrowser() && !checkIsWebview() && isAvailableForOpening) {
    showModal(area, (res) => {
      const handleClose = () => {
        if (isMustBeShownOnce && path) {
          markPathAsViewed(path, paths);
        }

        res();
      };

      return (
        <Modal
          toClose={res}
          className={s.mobileAppInfoModal}
          contentClassName={cx(s.mobileAppInfoModalContent, 'custom-mobile-app-info')}
          isOpen
          withoutCloseBtn
          noPadding
        >
          <div id={BANNER_ID}>
            <MobileModalContent path={path} handleClose={handleClose} />
          </div>
        </Modal>
      );
    });
  }
};

const MobileAppInfo = ({ proposal, accessToken, isButtonHidden = false }: IProps) => {
  const path = useProposalPathForMobileAppBuilder(proposal.id, proposal.type);
  const additionalPaths = useMemo(
    () => (proposal?.itineraryId ? [`itineraries/${proposal.itineraryId}`] : []),
    // it's really important to keep dependency only for proposal?.itineraryId
    // otherwise it leads to showing mobile banner every time we get new
    // object of proposal
    // the task - https://app.asana.com/0/1205677691765880/1206148057725392/f
    [proposal?.itineraryId],
  );
  const isWebview = checkIsWebview();
  const [mountPointReady, setMountPointReady] = useState(false);
  const allPaths = useMemo(() => {
    if (accessToken) {
      return [...additionalPaths, path, `guest-portals/${accessToken}`];
    }

    return [...additionalPaths, path];
  }, [additionalPaths, path, accessToken]);

  useEffect(() => {
    if (!isWebview) {
      const viewedProposalsStr = localStorage.getItem(VIEWED_MOBILE_APP_INFO);

      if (!viewedProposalsStr) {
        openMobileAppInfoModal(path, true, allPaths);
        return;
      }

      const viewedProposalsArr = JSON.parse(viewedProposalsStr);

      if (!viewedProposalsArr.includes(path) && !viewedProposalsArr.includes(`guest-portals/${accessToken}`)) {
        openMobileAppInfoModal(path, true, [...viewedProposalsArr, ...allPaths]);
      }
    }
  }, [isWebview, path, allPaths, accessToken, additionalPaths]);

  useEffect(() => {
    waitForElm(`#${MOB_APP_BUTTON_MOUNT_POINT}`).then(() => setMountPointReady(true));
  }, []);

  if (isWebview || isButtonHidden || !mountPointReady) {
    return null;
  }

  return (
    // @ts-ignore
    <RenderInDomNode id={MOB_APP_BUTTON_MOUNT_POINT}>
      <button
        type="button"
        className={cx(s.mobileAppInfoButton, 'custom-mobile-app-info-button')}
        onClick={() => openMobileAppInfoModal(path)}
      >
        <DeviceMobile size={22} />
      </button>
    </RenderInDomNode>
  );
};

export default withShowMobileInfoChecker(MobileAppInfo);

function withShowMobileInfoChecker<P extends IProps>(Comp: typeof MobileAppInfo) {
  return (props: P) => {
    if (props.proposal.showMobileInfo === false) {
      return null;
    }

    return <Comp {...props} />;
  };
}
